import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faUser, faCog, faBars, prefix } from "@fortawesome/free-solid-svg-icons";
import { ChromePicker } from "react-color";
import tinycolor from "tinycolor2";
import axios from "axios";

// Calculate color shades based on background color
const getColorShades = (bgColor) => {
    const color = tinycolor(bgColor);
    const isLight = color.isLight();
    const textColor = isLight ? "#333333" : "#f5f5f5";

    return {
        textColor,
        hoverColor: isLight ? "#0056b3" : "#e0e0e0",
        activeBgColor: isLight ? color.darken(10).toString() : color.lighten(10).toString(),
        hoverBgColor: isLight ? color.lighten(10).toString() : color.darken(10).toString(),
        dropdownBgColor: isLight ? color.lighten(20).toString() : color.darken(10).toString(),
        menuBgColor: isLight ? color.darken(2).toString() : color.darken(12).toString(),
        sidebarBgColor: isLight ? color.darken(18).toString() : color.darken(30).toString(),
        footerBgColor: isLight ? color.darken(12).toString() : color.darken(25).toString(),
        accentColor: color.spin(15).toString(),
        lightAccentColor: color.spin(15).lighten(30).toString(),
        darkAccentColor: color.spin(15).darken(20).toString(),
        shadowColor: color.darken(40).toString(),
    };
};

const Sidebar = styled.nav`
  background-color: ${(props) => props.colors.sidebarBgColor};
  color: ${(props) => props.colors.textColor};
  position: fixed;
  top: 0;
  left: ${(props) => (props.isOpen ? "0" : "-100%")};
  width: ${(props) => (props.isCollapsed ? "60px" : "260px")};
  height: 100%;
  z-index: 15;
  transition: transform 0.3s ease;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 0 20px;
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.colors.textColor};
  position: fixed;
  top: 0;
  left: ${(props) => (props.isSidebarOpen ? (props.isCollapsed ? "60px" : "260px") : "0")};
  width: ${(props) => (props.isSidebarOpen ? (props.isCollapsed ? "calc(100% - 60px)" : "calc(100% - 260px)") : "100%")};
  z-index: 15;
  height: 64px;
  transition: width 0.3s ease, left 0.3s ease;
  box-sizing: border-box;
  justify-content: space-between;
`;

const Footer = styled.footer`
  padding: 16px 20px;
  background-color: ${(props) => props.colors.footerBgColor};
  color: ${(props) => props.colors.textColor};
  text-align: center;
  height: 64px;
  box-sizing: border-box;
  width: 100%;
  transition: width 0.3s ease;
`;

const SubMenuLink = styled.button`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  text-decoration: none;
  background-color: ${(props) => (props.isActive ? props.colors.activeBgColor : "transparent")};
  font-size: ${(props) => (props.isCollapsed ? "14px" : "16px")};
  border-radius: 4px;
  transition: background-color 0.3s, color 0.3s;
  border: none;
  cursor: pointer;
  width: 100%;
  color: ${(props) => props.colors.textColor}; 

  &:hover {
    color: ${(props) => props.colors.hoverColor}; 
  }
`;

const SidebarHeader = styled.div`
  height: 64px;
  min-height: 64px;
  display: flex;
  align-items: center;
  padding: 0 15px;
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.textColor};
  position: relative;
  box-shadow: 0 2px 4px ${(props) => props.colors.shadowColor}; 
`;

const SubMenuTitle = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: ${(props) => props.colors.hoverBgColor};
  color: ${(props) => props.colors.textColor};
  border-radius: 4px;
  font-size: ${(props) => (props.isCollapsed ? "14px" : "16px")};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: ${(props) => props.colors.hoverBgColor};
  }
`;

const SidebarMenu = styled.ul`
  padding: 10px 0;
  list-style: none;
  margin: 0;
  background-color: ${(props) => props.colors.menuBgColor}; 
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: "Montserrat", sans-serif;
  margin-left: ${(props) => (props.isSidebarOpen ? (props.isCollapsed ? "60px" : "260px") : "0")};
  transition: margin-left 0.3s ease;
`;

const MainContent = styled.main`
  margin-top: 64px;
  padding: 20px;
  flex: 1;
  transition: margin-left 0.3s ease;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 768px) {
    margin-left: ${(props) => (props.isSidebarOpen ? (props.isCollapsed ? "60px" : "250px") : "0")};
    padding: 10px;
  }
`;

const SidebarTitle = styled.h3`
  margin-left: 10px;
  font-size: ${(props) => (props.isCollapsed ? "0" : "18px")};
  color: ${(props) => props.textColor};
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  transition: font-size 0.3s ease;

  @media (max-width: 768px) {
    font-size: ${(props) => (props.isCollapsed ? "14px" : "16px")};
  }
`;

const SidebarToggleButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.textColor};
  font-size: 20px;
  cursor: pointer;
  transition: color 0.3s ease, background-color 0.3s ease;
  border-radius: 50%;
  padding: 8px;
  margin-left: 20px;

  &:hover {
    color: ${(props) => tinycolor(props.bgColor).lighten(20).toString()};
    background-color: rgba(255, 255, 255, 0.1);
  }

  svg {
    vertical-align: middle;
  }
`;

const SubMenu = styled.ul`
  padding-left: ${(props) => (props.isCollapsed ? "0" : "20px")};
  list-style: none;
  margin: 0;
`;

const SubMenuItem = styled.li`
  margin-bottom: 4px;
`;

const Overlay = styled.div`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  z-index: 15;
`;

const ColorPickerContainer = styled.div`
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 26;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

  .picker-container {
    position: relative;
  }
`;

const ColorPickerButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.colors.textColor};
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 25;

  &:hover {
    background-color: ${(props) => props.colors.hoverBgColor};
  }
`;

const UserProfile = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  color: ${(props) => props.colors.textColor};
  cursor: pointer;
  margin-left: auto;

  .user-avatar {
    width: 40px;
    height: 40px;
    background-color: ${(props) => props.avatarColor};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ffffff;
    font-size: 18px;
    margin-right: 10px;
  }

  .dropdown-menu {
    display: ${(props) => (props.isOpen ? "block" : "none")};
    position: absolute;
    top: 50px;
    right: 0;
    background-color: ${(props) => props.colors.dropdownBgColor};
    color: ${(props) => props.colors.textColor};
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    width: 200px;
    z-index: 20;

    button {
      display: block;
      width: 100%;
      padding: 10px;
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      color: ${(props) => props.colors.textColor};

      &:hover {
        background-color: ${(props) => props.colors.hoverBgColor};
      }
    }
  }
`;

const NavBarBackend = ({ prefixTile="", menuData = [] }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [bgColor, setBgColor] = useState("#ABB2BB");
    const [colors, setColors] = useState(getColorShades("#ABB2BB"));
    const [auth, setAuth] = useState({ name: "", role: "" });

    const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);

    const handleColorChange = (color) => {
        const newBgColor = color.hex;
        setBgColor(newBgColor);
        setColors(getColorShades(newBgColor));
    };

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const renderMenuItems = (items) => items.map((item) => {
        if (item.children) {
            return (
                <div key={item.label}>
                    <SubMenuTitle isCollapsed={isCollapsed} colors={colors}>
                        {item.label}
                    </SubMenuTitle>
                    <SubMenu isCollapsed={isCollapsed}>
                        {renderMenuItems(item.children)}
                    </SubMenu>
                </div>
            );
        }
        return (
            <SubMenuItem key={item.key}>
                <SubMenuLink
                    isActive={location.pathname === item.key}
                    colors={colors}
                    isCollapsed={isCollapsed}
                    onClick={() => navigate(item.key)}
                >
                    {item.label}
                </SubMenuLink>
            </SubMenuItem>
        );
    });

    const LoginOut = () => {
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('lineBind');
        axios.post('/logout').then(() => navigate("/login")).catch(console.error);
    };

    const get_system_user = () => {
        axios.get('/system/user')
            .then(response => {
                let role_info = response.data[0] || {};
                let module_name = response.data.map(value => value.module_name).join(',');
                setAuth({ name: role_info.name, role: module_name });
            })
            .catch(console.error);
    };

    useEffect(() => {
        get_system_user();
        const handleResize = () => setIsCollapsed(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <AppContainer isSidebarOpen={isSidebarOpen} isCollapsed={isCollapsed} colors={colors}>
            <Header bgColor={bgColor} colors={colors} isSidebarOpen={isSidebarOpen} isCollapsed={isCollapsed}>
                <SidebarToggleButton
                    textColor={colors.textColor}
                    bgColor={bgColor}
                    onClick={toggleSidebar}
                >
                    <FontAwesomeIcon icon={isSidebarOpen ? faArrowLeft : faBars} />
                </SidebarToggleButton>
                <UserProfile
                    colors={colors}
                    avatarColor={bgColor}
                    isOpen={userMenuOpen}
                    onClick={toggleUserMenu}
                    className="desktop-user-profile"
                >
                    <div className="user-avatar">
                        <FontAwesomeIcon icon={faUser} />
                    </div>
                    <span className="username">{auth.name}</span>
                    <div className="dropdown-menu">
                        <button>修改個人資料</button>
                        <button onClick={LoginOut}>登出</button>
                    </div>
                </UserProfile>
            </Header>
            <Sidebar isOpen={isSidebarOpen} isCollapsed={isCollapsed} colors={colors}>
                <SidebarHeader bgColor={bgColor} textColor={colors.textColor} colors={colors}>
                    <SidebarTitle isCollapsed={isCollapsed} textColor={colors.textColor}>{prefixTile}</SidebarTitle>
                </SidebarHeader>
                <SidebarMenu colors={colors}>
                    {renderMenuItems(menuData)}
                </SidebarMenu>
            </Sidebar>
            <MainContent>
                <Outlet />
            </MainContent>
            <Footer colors={colors} isSidebarOpen={isSidebarOpen} isCollapsed={isCollapsed}>
                Footer Content
            </Footer>
            <Overlay isOpen={colorPickerOpen} onClick={() => setColorPickerOpen(false)} />
            <ColorPickerButton
                onClick={() => setColorPickerOpen(!colorPickerOpen)}
                bgColor={bgColor}
                colors={colors}
            >
                <FontAwesomeIcon icon={faCog} />
            </ColorPickerButton>
            {colorPickerOpen && (
                <ColorPickerContainer>
                    <div className="picker-container">
                        <ChromePicker color={bgColor} onChangeComplete={handleColorChange} />
                    </div>
                </ColorPickerContainer>
            )}
        </AppContainer>
    );
};

export default NavBarBackend;
